import { useCallback } from "react";
import Papa from "papaparse";

export interface ParsedOneRepMaxData {
  headers: string[]; // Array of "Category|ExerciseName" strings
  exerciseMetadata: Array<{
    index: number; // Column index in the CSV
    category: string; // Exercise category from row 1
    name: string; // Exercise name from row 2 (may include variant)
    fullKey: string; // "Category|ExerciseName" for unique identification
  }>;
  rows: Array<{
    athleteId: string;
    athleteName: string;
    values: Record<string, string | null>; // key is "Category|ExerciseName"
  }>;
}

function useOneRepMaxCsvParser() {
  const parseFile = useCallback((file: File): Promise<ParsedOneRepMaxData> => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: false, // Parse as array, not objects, since we have 2 header rows
        skipEmptyLines: true,
        complete: (results) => {
          const allRows = results.data as string[][];

          if (allRows.length < 3) {
            reject(new Error("CSV must contain at least 2 header rows and 1 data row"));
            return;
          }

          // Extract the two header rows
          const categoryRow = allRows[0];
          const exerciseNameRow = allRows[1];
          const dataRows = allRows.slice(2);

          // Validate required columns in second row
          if (exerciseNameRow[0] !== "AthleteId" || exerciseNameRow[1] !== "Athlete Name") {
            reject(new Error('CSV must have "AthleteId" and "Athlete Name" as first two columns in row 2'));
            return;
          }

          // Build exercise column metadata (combining category + name)
          const exerciseColumns: Array<{
            index: number;
            category: string;
            name: string;
            fullKey: string;
          }> = [];

          for (let i = 2; i < exerciseNameRow.length; i++) {
            const category = categoryRow[i]?.trim() || "";
            const name = exerciseNameRow[i]?.trim() || "";

            if (name) {
              exerciseColumns.push({
                index: i,
                category,
                name,
                fullKey: `${category}|${name}`,
              });
            }
          }

          if (exerciseColumns.length === 0) {
            reject(new Error("CSV must contain at least one exercise column"));
            return;
          }

          // Parse data rows
          const rows = dataRows.map((row) => {
            const values: Record<string, string | null> = {};

            exerciseColumns.forEach((col) => {
              const value = row[col.index]?.trim();
              values[col.fullKey] = value || null;
            });

            return {
              athleteId: row[0]?.trim() || "",
              athleteName: row[1]?.trim() || "",
              values,
            };
          });

          resolve({
            headers: exerciseColumns.map((col) => col.fullKey),
            exerciseMetadata: exerciseColumns,
            rows,
          });
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  }, []);

  return { parseFile };
}

export default useOneRepMaxCsvParser;
